import axios from "axios";

const MAX_ENTRIES = 300;
const listeners = new Set();
let requests = [];
let nextId = 1;
let installed = false;

export const isLocalhostEnvironment = () => {
    if (typeof window === "undefined") {
        return false;
    }

    const hostname = window.location.hostname;
    return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
};

const notify = () => {
    listeners.forEach((listener) => {
        listener(requests);
    });
};

const upsertRequest = (entry) => {
    const index = requests.findIndex((item) => item.id === entry.id);
    if (index === -1) {
        requests = [entry, ...requests].slice(0, MAX_ENTRIES);
    } else {
        requests = requests.map((item) => (item.id === entry.id ? { ...item, ...entry } : item));
    }
    notify();
};

const isAbortError = (error) =>
    error?.name === "AbortError"
    || error?.code === "ERR_CANCELED"
    || error?.code === "ECONNABORTED"
    || /aborted/i.test(error?.message || "");

const serializeBody = (body) => {
    if (body == null) {
        return "";
    }

    if (typeof body === "string") {
        return body.length > 4000 ? `${body.slice(0, 4000)}…` : body;
    }

    if (body instanceof FormData) {
        const parts = [];
        body.forEach((value, key) => {
            parts.push(`${key}=${typeof value === "string" ? value : "[binary]"}`);
        });
        return parts.join("&");
    }

    try {
        const serialized = JSON.stringify(body, null, 2);
        return serialized.length > 4000 ? `${serialized.slice(0, 4000)}…` : serialized;
    } catch {
        return String(body);
    }
};

const resolveFetchUrl = (input) => {
    if (typeof input === "string") {
        return input;
    }

    if (input instanceof URL) {
        return input.toString();
    }

    if (input && typeof input.url === "string") {
        return input.url;
    }

    return String(input);
};

const resolveAxiosUrl = (config) => {
    try {
        return axios.getUri(config);
    } catch {
        const baseURL = config.baseURL || "";
        const url = config.url || "";
        if (/^https?:\/\//i.test(url)) {
            return url;
        }
        return `${baseURL.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
    }
};

export const subscribeApiRequests = (listener) => {
    listeners.add(listener);
    listener(requests);

    return () => {
        listeners.delete(listener);
    };
};

export const getApiRequests = () => requests;

export const clearApiRequests = () => {
    requests = [];
    notify();
};

export const installApiRequestMonitor = () => {
    if (!isLocalhostEnvironment() || installed || window.__gpApiRequestMonitorInstalled) {
        return;
    }

    installed = true;
    window.__gpApiRequestMonitorInstalled = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
        const id = nextId++;
        const startedAt = Date.now();
        const method = (init.method || "GET").toUpperCase();
        const fullUrl = resolveFetchUrl(input);
        const requestBody = serializeBody(init.body);

        upsertRequest({
            id,
            source: "fetch",
            method,
            fullUrl,
            requestBody,
            status: "pending",
            startedAt,
        });

        try {
            const response = await originalFetch(input, init);
            upsertRequest({
                id,
                status: response.status,
                ok: response.ok,
                durationMs: Date.now() - startedAt,
                finishedAt: Date.now(),
            });
            return response;
        } catch (error) {
            if (isAbortError(error)) {
                upsertRequest({
                    id,
                    status: "cancelled",
                    ok: true,
                    errorMessage: "Requête annulée",
                    durationMs: Date.now() - startedAt,
                    finishedAt: Date.now(),
                });
            } else {
                upsertRequest({
                    id,
                    status: "error",
                    ok: false,
                    errorMessage: error?.message || "Network error",
                    durationMs: Date.now() - startedAt,
                    finishedAt: Date.now(),
                });
            }
            throw error;
        }
    };

    axios.interceptors.request.use((config) => {
        const id = nextId++;
        const startedAt = Date.now();
        const method = (config.method || "get").toUpperCase();
        const fullUrl = resolveAxiosUrl(config);

        config.metadata = {
            ...(config.metadata || {}),
            apiMonitorId: id,
            apiMonitorStartedAt: startedAt,
        };

        upsertRequest({
            id,
            source: "axios",
            method,
            fullUrl,
            requestBody: serializeBody(config.data),
            status: "pending",
            startedAt,
        });

        return config;
    });

    axios.interceptors.response.use(
        (response) => {
            const id = response.config?.metadata?.apiMonitorId;
            const startedAt = response.config?.metadata?.apiMonitorStartedAt || Date.now();

            if (id) {
                upsertRequest({
                    id,
                    status: response.status,
                    ok: response.status >= 200 && response.status < 300,
                    durationMs: Date.now() - startedAt,
                    finishedAt: Date.now(),
                });
            }

            return response;
        },
        (error) => {
            const config = error?.config;
            const id = config?.metadata?.apiMonitorId;
            const startedAt = config?.metadata?.apiMonitorStartedAt || Date.now();

            if (id) {
                if (isAbortError(error)) {
                    upsertRequest({
                        id,
                        status: "cancelled",
                        ok: true,
                        errorMessage: "Requête annulée",
                        durationMs: Date.now() - startedAt,
                        finishedAt: Date.now(),
                    });
                } else {
                    upsertRequest({
                        id,
                        status: error?.response?.status || "error",
                        ok: false,
                        errorMessage: error?.message || "Request failed",
                        durationMs: Date.now() - startedAt,
                        finishedAt: Date.now(),
                    });
                }
            }

            return Promise.reject(error);
        }
    );
};
